import { Injectable, OnDestroy } from '@angular/core';
import { AuthService } from '@auth0/auth0-angular';
import { Subject, BehaviorSubject, Subscription, firstValueFrom } from 'rxjs';
import { take } from 'rxjs/operators';
import { Capacitor } from '@capacitor/core';
import { LocalNotifications } from '@capacitor/local-notifications';
import { getGotifyConfig } from '../config/gotify.config';

export interface GotifyMessage {
  id: number;
  appid: number;
  message: string;
  title: string;
  priority: number;
  date: string;
  extras?: Record<string, unknown>;
}

/**
 * Manages a WebSocket connection to the Gotify notification stream.
 *
 * Lifecycle:
 *  - Connects automatically when Auth0 reports isAuthenticated = true.
 *  - Disconnects when the user logs out.
 *  - Reconnects with exponential back-off on unexpected disconnects.
 *  - Requests browser Notification permission on first login.
 *
 * Usage: inject this service anywhere to receive real-time notifications.
 *   messages$ — Observable stream of incoming Gotify messages.
 *   connected$ — BehaviorSubject<boolean> reflecting WebSocket state.
 */
@Injectable({ providedIn: 'root' })
export class GotifyService implements OnDestroy {
  readonly messages$ = new Subject<GotifyMessage>();
  readonly connected$ = new BehaviorSubject<boolean>(false);

  private ws: WebSocket | null = null;
  private reconnectDelay = 5_000;
  private readonly INITIAL_RECONNECT_DELAY = 5_000;
  private readonly MAX_RECONNECT_DELAY = 300_000; // 5 min (Gotify is often flaky)
  private readonly MAX_CONSECUTIVE_FAILURES = 12; // stop after ~30 min of attempts
  private consecutiveFailures = 0;
  private gaveUp = false;
  private readonly authSub: Subscription;
  private intentionalDisconnect = false;
  /** Latch: `connect()` es async desde que espera al token de Auth0, y en esa ventana el
   *  guard de `readyState` aún no ve nada — dos disparos (login + reintento) abrirían dos
   *  sockets y solo se cerraría el último. */
  private connecting = false;

  constructor(private readonly auth: AuthService) {
    this.authSub = this.auth.isAuthenticated$.subscribe(isAuthenticated => {
      if (isAuthenticated) {
        this.intentionalDisconnect = false;
        void this.connect();
        this.requestNotificationPermission();
      } else {
        this.intentionalDisconnect = true;
        this.disconnect();
      }
    });
  }

  // ─── WebSocket lifecycle ─────────────────────────────────────────────────

  private async connect(): Promise<void> {
    if (
      this.connecting ||
      (this.ws &&
        (this.ws.readyState === WebSocket.OPEN ||
          this.ws.readyState === WebSocket.CONNECTING))
    ) {
      return;
    }
    this.connecting = true;
    try {
      await this.abrirSocket();
    } finally {
      this.connecting = false;
    }
  }

  private async abrirSocket(): Promise<void> {
    const { serverUrl, clientToken } = getGotifyConfig();

    if (!clientToken || clientToken === 'REPLACE_WITH_GOTIFY_CLIENT_TOKEN') {
      console.warn(
        '[Gotify] Client token not configured — skipping WebSocket connection. ' +
        'Set the token in core/config/gotify.config.ts after deploying Gotify.'
      );
      return;
    }

    // DOS credenciales, y hacen falta las dos:
    //  · `token`        — el client token de Gotify, que autoriza a LEER el stream. Lo
    //                     valida Gotify, al final del camino.
    //  · `access_token` — el Bearer de Auth0. Lo valida APISIX, que en SG-2 está delante de
    //                     `gotify.shared.n.synapseguard.com` como de todo lo demás y no
    //                     exime ninguna ruta (§4.3). Sin él, el handshake ni llega a Gotify.
    //
    // ⚠️ Va en la query y no en una cabecera porque el `WebSocket` del navegador NO permite
    // cabeceras en el handshake — es la API, no una elección. Las dos alternativas son
    // peores: `Sec-WebSocket-Protocol` obliga al servidor a devolver el subprotocolo o el
    // navegador tumba la conexión, y Gotify no lo hace. Contrapartida asumida: el JWT queda
    // en los logs de acceso del borde. Requiere que APISIX copie `access_token` a
    // `Authorization` ANTES de `openid-connect` (prioridad > 260) — coordinado con infra.
    const bearer = await this.auth0Bearer();
    if (!bearer) {
      console.warn('[Gotify] Sin token de Auth0 todavía — el borde rechazaría el handshake; se reintenta.');
      this.scheduleReconnect();
      return;
    }

    const base = serverUrl.replace(/^https?/, m => (m === 'https' ? 'wss' : 'ws'));
    const wsUrl =
      `${base}/stream?token=${encodeURIComponent(clientToken)}&access_token=${encodeURIComponent(bearer)}`;

    try {
      this.ws = new WebSocket(wsUrl);

      this.ws.onopen = () => {
        this.reconnectDelay = this.INITIAL_RECONNECT_DELAY;
        this.consecutiveFailures = 0;
        this.gaveUp = false;
        this.connected$.next(true);
        console.info('[Gotify] Connected to notification stream.');
      };

      this.ws.onmessage = (event: MessageEvent) => {
        try {
          const msg: GotifyMessage = JSON.parse(event.data as string);
          this.messages$.next(msg);
          // showNotification is async — catch any rejection to avoid unhandled Promise
          this.showNotification(msg).catch(err =>
            console.error('[Gotify] Notification error:', err)
          );
        } catch {
          console.warn('[Gotify] Could not parse incoming message:', event.data);
        }
      };

      this.ws.onerror = () => {
        // onerror is always followed by onclose; handle reconnect there.
        // Only log on first few failures to avoid console spam (the
        // backoff timer below will keep retrying silently).
        if (this.consecutiveFailures < 3) {
          console.warn(
            `[Gotify] WebSocket error (attempt ${this.consecutiveFailures + 1}) — will retry with backoff.`
          );
        }
      };

      this.ws.onclose = () => {
        this.connected$.next(false);
        if (!this.intentionalDisconnect) {
          this.scheduleReconnect();
        }
      };
    } catch (error) {
      console.error('[Gotify] Failed to open WebSocket:', error);
    }
  }

  private disconnect(): void {
    if (this.ws) {
      // Suppress the onclose handler to prevent an unintended reconnect.
      this.ws.onclose = null;
      this.ws.close();
      this.ws = null;
    }
    this.connected$.next(false);
  }

  private scheduleReconnect(): void {
    if (this.gaveUp) {
      return;
    }
    this.consecutiveFailures += 1;
    if (this.consecutiveFailures >= this.MAX_CONSECUTIVE_FAILURES) {
      this.gaveUp = true;
      console.warn(
        `[Gotify] Reached ${this.MAX_CONSECUTIVE_FAILURES} consecutive failures — ` +
        'giving up reconnect attempts. Refresh the page once Gotify is back online.'
      );
      return;
    }
    this.auth.isAuthenticated$.pipe(take(1)).subscribe(isAuth => {
      if (isAuth && !this.intentionalDisconnect) {
        setTimeout(() => void this.connect(), this.reconnectDelay);
        this.reconnectDelay = Math.min(
          this.reconnectDelay * 2,
          this.MAX_RECONNECT_DELAY
        );
      }
    });
  }

  /** Bearer de Auth0 para el handshake, o `''` si la sesión no puede emitirlo.
   *
   *  Se re-lee en CADA conexión (no se cachea): el stream se reconecta con backoff durante
   *  hasta media hora, y un token caducado en el handshake es un 401 del que no se sale.
   *  APISIX valida solo al abrir, así que la conexión ya establecida sobrevive a la
   *  caducidad — solo importa el momento del handshake.
   */
  private async auth0Bearer(): Promise<string> {
    try {
      return (await firstValueFrom(this.auth.getAccessTokenSilently())) ?? '';
    } catch {
      return '';
    }
  }

  // ─── Notifications ───────────────────────────────────────────────────────

  private async requestNotificationPermission(): Promise<void> {
    if (Capacitor.isNativePlatform()) {
      // Android / iOS: use the Capacitor LocalNotifications API.
      // This shows the native OS permission dialog on Android 13+ (API 33+).
      const result = await LocalNotifications.requestPermissions();
      if (result.display !== 'granted') {
        console.warn('[Gotify] Local notification permission not granted:', result.display);
      }
    } else if ('Notification' in globalThis && Notification.permission === 'default') {
      // Web browser fallback.
      await Notification.requestPermission();
    }
  }

  private async showNotification(msg: GotifyMessage): Promise<void> {
    if (Capacitor.isNativePlatform()) {
      await this.showNativeNotification(msg);
    } else {
      this.showBrowserNotification(msg);
    }
  }

  private async showNativeNotification(msg: GotifyMessage): Promise<void> {
    try {
      const perm = await LocalNotifications.checkPermissions();
      if (perm.display !== 'granted') return;

      // Capacitor LocalNotifications requires a positive int32 id.
      const notifId = Math.abs(msg.id) % 2_147_483_647 || 1;

      await LocalNotifications.schedule({
        notifications: [
          {
            title: msg.title,
            body: msg.message,
            id: notifId,
            // smallIcon matches the drawable resource in android/app/src/main/res/
            smallIcon: 'ic_launcher_foreground',
            iconColor: '#00b4d8',
            // actionTypeId groups notifications so Android can collapse them
            actionTypeId: 'GOTIFY_ALERT',
          },
        ],
      });
    } catch (error) {
      console.error('[Gotify] Failed to schedule local notification:', error);
    }
  }

  private showBrowserNotification(msg: GotifyMessage): void {
    if (!('Notification' in globalThis) || Notification.permission !== 'granted') {
      return;
    }
    try {
      new Notification(msg.title, {
        body: msg.message,
        icon: '/assets/img/Icono1_FondoNegro.svg',
        tag: `gotify-${msg.id}`,
      });
    } catch {
      // Notification API unavailable in some sandboxed contexts.
    }
  }

  // ─── Cleanup ─────────────────────────────────────────────────────────────

  ngOnDestroy(): void {
    this.authSub.unsubscribe();
    this.intentionalDisconnect = true;
    this.disconnect();
    this.messages$.complete();
    this.connected$.complete();
  }
}
